// frontend/src/pages/ClientDetailPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  apiGet, apiGetAll, apiPatch,
  getClientUpdates, getTaskTypes, getClientGroups, getClientsInGroup, resolveClientGroup,
  getOtherTasks,
} from '../api';
import useClientRollup from '../hooks/useClientRollup';
import { countedTimeEntries, tileDrilldown, yearEndForDate } from '../utils/rollup';
import { describeRange, sinceLastInvoiced } from '../utils/dateRange';
import DateRangeFilter from '../components/rollup/DateRangeFilter';
import {
  BreakdownByClientTable, DetailModal, GroupDistributionDonut, IncompleteDataBanner,
  MetricTiles, SummaryByYearTable, TimeByCodePie, TimeByEmployeePie, ViewModeToggle,
} from '../components/rollup';
import NewTaskModal from '../components/NewTaskModal';
import TimelineDrawer from '../components/TimelineDrawer';
import RollForwardModal from '../components/rollforward/RollForwardModal';
import { canManage } from '../utils/roles';
import { addMasterFile, useMasterFileConfigured } from '../components/masterFile';

const TABS = ['Tasks', 'Invoices', 'Time Entries', 'Files', 'Updates'];

// Matches the labels used everywhere else recurrence is shown (OtherTasksTable's
// recurrence dropdowns, OtherTaskDetailPage's RECURRENCE_OPTIONS) -- 'annual'
// reads as 'Annually', not a capitalised 'Annual'.
const RECURRENCE_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Annually' };

export default function ClientDetailPage({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState('Tasks');
  const [tasks, setTasks] = useState([]);
  const [files, setFiles] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [detailModal, setDetailModal] = useState(null);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [taskTypeOptions, setTaskTypeOptions] = useState([]);
  const [rollForwardScope, setRollForwardScope] = useState(null);

  // Master file: the outcome of New Client's "Add Master File" arrives in the
  // navigation state; the Files tab's button reports through the same banner.
  const location = useLocation();
  const [masterFileNotice, setMasterFileNotice] = useState(
    () => location.state?.masterFile || null,
  );
  const [creatingMasterFile, setCreatingMasterFile] = useState(false);
  const masterFileConfigured = useMasterFileConfigured(client ? Boolean(client.is_corporate) : undefined);

  const [viewMode, setViewMode] = useState('client');       // 'client' | 'group'
  const [groupMetric, setGroupMetric] = useState('time');   // donut switcher
  const [groupMemberIds, setGroupMemberIds] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupMembersFailed, setGroupMembersFailed] = useState(false);

  // Page-wide date range ('' = open-ended on that side; both '' = all time).
  // Local state, like `tab`: this page keeps none of its state in the URL.
  const [range, setRange] = useState({ startDate: '', endDate: '' });

  useEffect(() => {
    getTaskTypes().then(setTaskTypeOptions).catch(() => setTaskTypeOptions([]));
  }, []);

  useEffect(() => {
    apiGet(`/api/clients/${id}/`).then(c => {
      setClient(c);
      setForm({
        name: c.name || '',
        contact: c.contact || '',
        bill_to: c.bill_to || '',
        email: c.email || '',
        phone: c.phone || '',
        // The four fields an invoice actually prints. `address` is a legacy
        // free-text column nothing renders; it is carried through untouched so
        // saving here does not quietly discard it.
        address: c.address || '',
        street_address: c.street_address || '',
        city: c.city || '',
        province: c.province || '',
        postal_code: c.postal_code || '',
        group: c.group ?? null,
        is_corporate: !!c.is_corporate,
        is_active: c.is_active !== false,
        fiscal_month_end: c.fiscal_month_end || '',
        uses_qbo_online: !!c.uses_qbo_online,
      });
    });
  }, [id]);

  // Group membership comes from the FK, not V1's string matching
  // (`clients.filter(c => c.group_name === client.group_name)`, which fetched
  // every client in the system to compare a free-text field). A client with
  // no group has no group view at all.
  useEffect(() => {
    if (!client?.group) {
      setGroupMemberIds([]);
      setGroupMembersFailed(false);
      setViewMode('client');
      return;
    }
    setGroupMembersFailed(false);
    getClientsInGroup(client.group)
      .then((members) => setGroupMemberIds(members.map((m) => m.id)))
      .catch(() => {
        // Don't let this look like a small group: the toggle still renders
        // (it only checks groupName) and rollupIds silently falls back to
        // single-client data, which reads as a legitimate — just small —
        // group roll-up. Flag it instead so the failure is visible.
        setGroupMemberIds([]);
        setGroupMembersFailed(true);
      });
  }, [client?.group]);

  useEffect(() => {
    getClientGroups().then(setGroupOptions).catch(() => setGroupOptions([]));
  }, []);

  // Loads both kinds of task for the Tasks tab: year-end (T1/T2) tasks and
  // bookkeeping (OtherTask) rows. Shared by the tab-load effect below and by
  // the New Task modal's post-create refresh, so creating a year-end task
  // doesn't wipe the bookkeeping rows already on screen.
  const loadTasks = useCallback(() => {
    // A client's detail page is a history view, not a work queue -- hiding
    // their forward-planned tasks here would be a regression.
    //
    // Both kinds: a client's work is not only their year end. Bookkeeping
    // (OtherTask) rows have always been linkable to a client and were simply
    // never fetched here. Each fetch catches its own failure so a broken
    // other-tasks response still leaves the year-end list rendered rather
    // than blanking the whole tab.
    return Promise.all([
      apiGetAll(`/api/tasks/?client=${id}&horizon=all`).catch(() => []),
      getOtherTasks({ client: id, horizon: 'all' }).catch(() => []),
    ]).then(([yearEnd, other]) => {
      setTasks([
        ...yearEnd.map((t) => ({ ...t, _kind: 'task' })),
        ...other.map((t) => ({ ...t, _kind: 'other' })),
      ]);
    });
  }, [id]);

  // Per-tab fetches for data not covered by the roll-up.
  useEffect(() => {
    if (!id) return;
    if (tab === 'Tasks') {
      loadTasks();
    } else if (tab === 'Files') {
      apiGetAll(`/api/client-files/?client=${id}`).then(setFiles).catch(() => setFiles([]));
    } else if (tab === 'Updates') {
      getClientUpdates(id).then(setUpdates).catch(() => setUpdates([]));
    }
  }, [id, tab, loadTasks]);

  const rollupIds = useMemo(() => {
    if (viewMode === 'group' && groupMemberIds.length) return groupMemberIds;
    return id ? [Number(id)] : [];
  }, [viewMode, groupMemberIds, id]);

  const rollup = useClientRollup(rollupIds, range);

  // Always the VIEWED client's own last invoice, in Group mode too: an
  // invoice belongs to one client (Invoice.client), each member is billed
  // separately, and nothing on this page treats the group as one billing
  // unit -- "since the group's newest invoice" would hide a sibling's
  // older, still-unbilled time.
  const since = useMemo(
    () => sinceLastInvoiced(rollup.allTime.invoices, Number(id)),
    [rollup.allTime.invoices, id],
  );
  const sinceApplied = !!since
    && range.startDate === since.startDate && range.endDate === since.endDate;
  const rangeWords = describeRange(range.startDate, range.endDate).replace(/^Showing /, '');

  const saveClient = async () => {
    const updated = await apiPatch(`/api/clients/${id}/`, form);
    setClient(updated);
    setEditing(false);
  };

  const createMasterFileNow = async () => {
    setCreatingMasterFile(true);
    const result = await addMasterFile(id);
    setMasterFileNotice(result);
    setCreatingMasterFile(false);
    if (result.ok) {
      apiGetAll(`/api/client-files/?client=${id}`).then(setFiles).catch(() => {});
    }
  };

  if (!client) return <div style={{ padding: 24 }}>Loading…</div>;

  return (
    <div style={{ padding: 24 }}>
      <Link to="/clients" style={{ color: '#6b7280', textDecoration: 'none' }}>← Clients</Link>
      {masterFileNotice && (
        <div role="status" style={{
          margin: '12px 0', padding: '10px 14px', borderRadius: 8, fontSize: 14,
          display: 'flex', alignItems: 'center', gap: 12,
          background: masterFileNotice.ok ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${masterFileNotice.ok ? '#a7f3d0' : '#fecaca'}`,
          color: masterFileNotice.ok ? '#065f46' : '#991b1b',
        }}>
          <span style={{ flex: 1 }}>
            {masterFileNotice.ok ? 'Master file: ' : 'Master file not created: '}
            {masterFileNotice.message}
          </span>
          {masterFileNotice.ok && masterFileNotice.file?.sharepoint_url && (
            <a href={masterFileNotice.file.sharepoint_url} target="_blank" rel="noopener noreferrer"
               style={{ color: '#065f46', fontWeight: 600 }}>Open it</a>
          )}
          <button type="button" aria-label="Dismiss" onClick={() => setMasterFileNotice(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16 }}>×</button>
        </div>
      )}
      <button
        onClick={() => setHistoryOpen(true)}
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 900,
          padding: '10px 16px', border: '1px solid #d1d5db', borderRadius: 999,
          background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
          color: '#374151', boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
        }}
        title="View this client's history"
      >
        🕑 History
      </button>
      <TimelineDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        recordType="client"
        recordId={id}
        title={`History · ${client.name}`}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}>{client.name}</h1>
          {client.uses_qbo_online && (
            <span
              data-testid="qbo-online-badge"
              title="This client keeps its own books in QuickBooks Online"
              style={{
                padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0',
              }}
            >
              QuickBooks Online
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canManage(user) && (
            <button onClick={() => setRollForwardScope({ kind: 'client', client_id: Number(id) })} style={btnStyle}>
              Roll forward this client
            </button>
          )}
          <button onClick={() => setEditing(e => !e)} style={btnStyle}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
        </div>
      </div>

      <ViewModeToggle
        mode={viewMode}
        groupName={client.group_name}
        onChange={setViewMode}
      />

      {/* Identity card */}
      <div style={{ background: '#f9fafb', padding: 16, borderRadius: 6, margin: '16px 0 24px', border: '1px solid #e5e7eb' }}>
        {editing ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} />
            <Field label="Contact" value={form.contact} onChange={v => setForm(f => ({ ...f, contact: v }))} />
            <Field label="Bill To" value={form.bill_to} onChange={v => setForm(f => ({ ...f, bill_to: v }))} />
            <Field label="Email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} />
            <Field label="Phone" value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} />
            <Field label="Street address" value={form.street_address} onChange={v => setForm(f => ({ ...f, street_address: v }))} />
            <Field label="City" value={form.city} onChange={v => setForm(f => ({ ...f, city: v }))} />
            <Field label="Province" value={form.province} onChange={v => setForm(f => ({ ...f, province: v }))} />
            <Field label="Postal code" value={form.postal_code} onChange={v => setForm(f => ({ ...f, postal_code: v }))} />
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, color: '#374151' }}>
              <span style={{ marginBottom: 4 }}>Group</span>
              <select
                value={form.group ?? ''}
                onChange={async (e) => {
                  const { value } = e.target;
                  if (value === '__new__') {
                    // eslint-disable-next-line no-alert
                    const name = window.prompt('New group name');
                    if (!name?.trim()) return;
                    const groupId = await resolveClientGroup(name);
                    setGroupOptions(await getClientGroups());
                    setForm((f) => ({ ...f, group: groupId }));
                    return;
                  }
                  setForm((f) => ({ ...f, group: value ? Number(value) : null }));
                }}
                style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 14 }}
              >
                <option value="">— No group —</option>
                {groupOptions.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
                <option value="__new__">＋ New group…</option>
              </select>
            </label>
            <Field label="Fiscal Month End (1-12)" value={form.fiscal_month_end} onChange={v => setForm(f => ({ ...f, fiscal_month_end: v }))} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={!!form.is_corporate}
                     onChange={e => setForm(f => ({ ...f, is_corporate: e.target.checked }))} />
              Corporate
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={!!form.is_active}
                     onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              Active
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                   title="The client keeps its own books in QuickBooks Online">
              <input type="checkbox" checked={!!form.uses_qbo_online}
                     onChange={e => setForm(f => ({ ...f, uses_qbo_online: e.target.checked }))} />
              Uses QuickBooks Online
            </label>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={saveClient} style={{ ...btnStyle, background: '#059669', color: 'white', borderColor: '#059669' }}>Save</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 14 }}>
            <Row label="Contact" value={client.contact} />
            <Row label="Bill To" value={client.bill_to} />
            <Row label="Email" value={client.email} />
            <Row label="Phone" value={client.phone} />
            <Row label="Address" value={<ClientAddress client={client} />} />
            <Row label="Group" value={client.group_name} />
            <Row label="Entity Type" value={client.is_corporate ? 'Corporate' : 'Personal'} />
            <Row label="Fiscal Month End" value={client.fiscal_month_end} />
            <Row label="Active" value={client.is_active ? 'Yes' : 'No'} />
            <Row label="Books in QuickBooks Online" value={client.uses_qbo_online ? 'Yes' : 'No'} />
          </div>
        )}
      </div>

      {groupMembersFailed && (
        <div
          role="alert"
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 20,
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          <strong>Could not load this client&apos;s group.</strong>
          <div style={{ marginTop: 4 }}>
            Group membership failed to load, so &quot;Group: {client.group_name}&quot;
            (if selected) is showing {client.name} alone, not the full group.
          </div>
        </div>
      )}

      {rollup.partial && (
        <IncompleteDataBanner
          failedClientIds={rollup.failedClientIds}
          failedFetches={rollup.failedFetches}
        />
      )}

      <DateRangeFilter
        startDate={range.startDate}
        endDate={range.endDate}
        onChange={(startDate, endDate) => setRange({ startDate, endDate })}
        since={since}
        sinceApplied={sinceApplied}
        sinceDisabledReason={rollup.loading ? 'Loading invoices…' : 'No invoices yet'}
      />

      <MetricTiles
        metrics={rollup.metrics}
        // A/R is what the client owes today, so it never narrows to the range
        // (see useClientRollup); its drill-down reads the all-time rows to match.
        captions={rollup.rangeActive ? { ar: 'All dates · as of today' } : undefined}
        onTileClick={(tile) => {
          if (tile === 'ar') {
            const d = tileDrilldown('ar', rollup.allTime);
            setDetailModal(rollup.rangeActive ? { ...d, scopeLabel: 'As of today' } : d);
            return;
          }
          const d = tileDrilldown(tile, rollup);
          setDetailModal(rollup.rangeActive ? { ...d, scopeLabel: rangeWords } : d);
        }}
      />

      <SummaryByYearTable rows={rollup.yearRows} onCellClick={setDetailModal} />

      <TimeByEmployeePie data={rollup.employeeData} />
      <TimeByCodePie data={rollup.codeData} />

      {viewMode === 'group' && (
        <>
          <GroupDistributionDonut
            data={rollup.clientRows}
            metric={groupMetric}
            onMetricChange={setGroupMetric}
          />
          <BreakdownByClientTable
            rows={rollup.clientRows}
            onClientClick={(clientId) => navigate(`/clients/${clientId}`)}
            onCellClick={setDetailModal}
            timeEntries={rollup.timeEntries}
            invoices={rollup.invoices}
            writeOffs={rollup.writeOffs}
          />
        </>
      )}

      {detailModal && (
        <DetailModal modal={detailModal} onClose={() => setDetailModal(null)} navigate={navigate} />
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
        {TABS.map(t => (
          <button key={t}
                  onClick={() => setTab(t)}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    background: 'transparent',
                    borderBottom: tab === t ? '2px solid #059669' : '2px solid transparent',
                    color: tab === t ? '#059669' : '#6b7280',
                    fontWeight: tab === t ? 600 : 500,
                    cursor: 'pointer',
                  }}>{t}</button>
        ))}
      </div>

      {tab === 'Tasks' && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <button onClick={() => setShowNewTaskModal(true)} style={btnStyle}>+ New Task</button>
          </div>
          <DataTable rows={tasks}
                     emptyText="No tasks yet."
                     columns={[
                       { header: 'Kind',
                         render: r => (r._kind === 'other' ? 'Bookkeeping' : 'Year End') },
                       { header: 'Name',
                         render: r => r.name || r.task_type_name || r.task_type || '—' },
                       { header: 'Year', accessor: 'fiscal_year' },
                       { header: 'Status', accessor: 'status_name' },
                       { header: 'Due', accessor: 'due_date' },
                       { header: 'Repeats',
                         render: r => (r._kind === 'other' && r.recurrence && r.recurrence !== 'none'
                           ? RECURRENCE_LABELS[r.recurrence] || r.recurrence : '') },
                       { header: 'Done', render: r => r.is_completed ? '✓' : '' },
                     ]}
                     onRowClick={r => navigate(
                       r._kind === 'other' ? `/other-tasks/${r.id}` : `/tasks/${r.id}`,
                     )} />
        </div>
      )}

      {tab === 'Invoices' && rollup.rangeActive && (
        <DataTable rows={newestFirst(rollup.invoices)}
                   emptyText="No invoices in this period."
                   onRowClick={r => navigate(`/invoices/${r.id}`)}
                   columns={[
                     ...(viewMode === 'group' ? [clientColumn(rollup.clientsById)] : []),
                     ...INVOICE_COLUMNS,
                   ]} />
      )}

      {tab === 'Invoices' && !rollup.rangeActive && (
        <GroupedDataTable rows={rollup.invoices}
                          client={client}
                          clientsById={rollup.clientsById}
                          dateField="date"
                          amountField="total"
                          emptyText="No invoices."
                          onRowClick={r => navigate(`/invoices/${r.id}`)}
                          columns={INVOICE_COLUMNS} />
      )}

      {tab === 'Time Entries' && rollup.rangeActive && (
        <PeriodTimeEntries
          rows={countedTimeEntries(rollup.timeEntries)}
          columns={[
            ...(viewMode === 'group' ? [clientColumn(rollup.clientsById)] : []),
            ...TIME_COLUMNS,
          ]}
        />
      )}

      {tab === 'Time Entries' && !rollup.rangeActive && (
        <GroupedDataTable rows={countedTimeEntries(rollup.timeEntries)}
                          client={client}
                          clientsById={rollup.clientsById}
                          dateField="date"
                          amountField="billable_amount"
                          emptyText="No time entries."
                          columns={TIME_COLUMNS} />
      )}

      {tab === 'Files' && (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
          <button type="button" onClick={createMasterFileNow}
                  disabled={!masterFileConfigured || creatingMasterFile}
                  title={masterFileConfigured ? undefined
                    : 'A manager needs to pick the master file folder and template in Settings → Master files.'}
                  style={{ ...btnStyle, opacity: masterFileConfigured ? 1 : 0.5 }}>
            {creatingMasterFile ? 'Creating master file…' : 'Create Master File'}
          </button>
          {masterFileConfigured === false && (
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              Master files are not set up yet (Settings → Master files).
            </span>
          )}
        </div>
      )}

      {tab === 'Files' && (
        <DataTable rows={files}
                   emptyText="No files."
                   columns={[
                     {
                       header: 'Name',
                       render: (f) => {
                         const href = f.sharepoint_url || f.file || null;
                         if (href) {
                           return (
                             <a href={href} target="_blank" rel="noopener noreferrer"
                                style={{ color: '#0073ea', textDecoration: 'none' }}>
                               {f.name || '—'}
                             </a>
                           );
                         }
                         return f.name || '—';
                       },
                     },
                     {
                       header: 'Source',
                       render: (f) => {
                         if (f.sharepoint_url || f.sharepoint_id) {
                           return (
                             <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#1d4ed8' }}>
                               <span aria-hidden="true">🔗</span> SharePoint
                             </span>
                           );
                         }
                         if (f.file) return 'Upload';
                         return f.file_type || '—';
                       },
                     },
                     { header: 'Uploaded by', render: (f) => f.uploaded_by_name || '—' },
                     {
                       header: 'Linked',
                       render: (f) => (f.created_at ? (f.created_at || '').slice(0, 10) : '—'),
                     },
                   ]} />
      )}

      {tab === 'Updates' && (
        <DataTable rows={updates}
                   emptyText="No updates."
                   columns={[
                     { header: 'When', accessor: 'created_at' },
                     { header: 'Author', render: r => r.author_name || r.created_by_name || '—' },
                     { header: 'Update', accessor: 'content' },
                   ]} />
      )}

      {showNewTaskModal && (
        <NewTaskModal
          clientId={id}
          clientName={client?.name}
          isCorporate={client?.is_corporate}
          fiscalMonthEnd={client?.fiscal_month_end}
          taskTypeOptions={taskTypeOptions}
          onClose={() => setShowNewTaskModal(false)}
          onCreated={async (task) => {
            setShowNewTaskModal(false);
            // Refresh both task kinds -- same history view as the tab load
            // above, so it needs the same horizon=all or it would drop
            // forward-planned tasks right after creating one. Reusing
            // loadTasks also keeps the bookkeeping rows already on screen
            // instead of wiping them with a year-end-only fetch.
            await loadTasks();
            // Navigate to the new task's detail page
            navigate(`/tasks/${task.id}`);
          }}
        />
      )}

      <RollForwardModal
        scope={rollForwardScope}
        onClose={() => setRollForwardScope(null)}
      />
    </div>
  );
}

const fmtCAD = (v) => (v != null
  ? parseFloat(v).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' }) : '—');

const INVOICE_COLUMNS = [
  { header: 'Number', accessor: 'number' },
  { header: 'Date', accessor: 'date' },
  { header: 'Total', render: r => fmtCAD(r.total) },
  { header: 'Status', accessor: 'status' },
];

// task_name names whichever task the line is booked to -- a year-end Task or,
// when only other_task is set, the Other (bookkeeping) task.
const TIME_COLUMNS = [
  { header: 'Date', accessor: 'date' },
  { header: 'Hours', accessor: 'hours' },
  { header: 'Code', accessor: 'time_code' },
  { header: 'Task', render: r => r.task_name || r.other_task_name || '—' },
  { header: 'Who', accessor: 'user_name' },
  { header: 'Amount', render: r => fmtCAD(r.billable_amount) },
  { header: 'Notes', accessor: 'notes' },
];

// Group mode mixes every member's rows; a flat list has no per-client heading,
// so it names the client on each row instead.
const clientColumn = (clientsById) => ({
  header: 'Client',
  render: r => clientsById?.[r.client]?.name || '—',
});

// Newest date first; same-day rows newest-created first. ISO dates sort as text.
function newestFirst(rows) {
  return [...(rows || [])].sort((a, b) => {
    const da = a.date || '';
    const db = b.date || '';
    if (da !== db) return da < db ? 1 : -1;
    return (b.id ?? 0) - (a.id ?? 0);
  });
}

const fmtHours = (n) => String(Math.round(n * 100) / 100);

/** Time Entries inside a date range: a totals strip over a flat list. */
function PeriodTimeEntries({ rows, columns }) {
  if (!rows.length) {
    return <div style={{ color: '#9ca3af', padding: 24, textAlign: 'center' }}>No time in this period.</div>;
  }
  let hours = 0;
  let billableHours = 0;
  let amount = 0;
  for (const r of rows) {
    const h = parseFloat(r.hours) || 0;
    hours += h;
    // TimeEntry.billable_code defaults to BILLABLE; only NON_BILLABLE is not.
    if ((r.billable_code || 'BILLABLE') === 'BILLABLE') billableHours += h;
    amount += parseFloat(r.billable_amount) || 0;
  }
  const stats = [
    ['Total hours', fmtHours(hours)],
    ['Billable hours', fmtHours(billableHours)],
    ['Non-billable hours', fmtHours(hours - billableHours)],
    ['Billable amount', fmtCAD(amount)],
  ];
  return (
    <>
      <div data-testid="period-totals"
           style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12, fontSize: 13, color: '#6b7280' }}>
        {stats.map(([label, value]) => (
          <div key={label}>
            <div>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{value}</div>
          </div>
        ))}
      </div>
      <DataTable rows={newestFirst(rows)} columns={columns} emptyText="No time in this period." />
    </>
  );
}

/** The address as an invoice prints it: street, then "City, PROV  POSTAL". */
function ClientAddress({ client }) {
  const cityLine =
    [client.city, client.province].filter(Boolean).join(', ') +
    (client.postal_code ? `  ${client.postal_code}` : '');
  const lines = [client.street_address, cityLine]
    .map(v => (v || '').trim())
    .filter(Boolean);
  // Fall back to the legacy free-text column so an address entered before the
  // structured fields existed still shows, rather than reading as missing.
  if (!lines.length) return client.address ? <div>{client.address}</div> : null;
  return (
    <>
      {lines.map((line, i) => (
        <div key={i}>{line}</div>
      ))}
    </>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div>{value || '—'}</div>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, color: '#374151' }}>
      <span style={{ marginBottom: 4 }}>{label}</span>
      <input value={value ?? ''}
             onChange={e => onChange(e.target.value)}
             style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 14 }} />
    </label>
  );
}

function DataTable({ rows, columns, emptyText, onRowClick }) {
  if (!rows || rows.length === 0) {
    return <div style={{ color: '#9ca3af', padding: 24, textAlign: 'center' }}>{emptyText}</div>;
  }
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            {columns.map((c, i) => (
              <th key={i} style={th}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r._kind ? `${r._kind}-${r.id ?? i}` : (r.id ?? i)}
                style={{ borderBottom: '1px solid #f3f4f6', cursor: onRowClick ? 'pointer' : 'default' }}
                onClick={onRowClick ? () => onRowClick(r) : undefined}>
              {columns.map((c, j) => (
                <td key={j} style={td}>{c.render ? c.render(r) : (r[c.accessor] ?? '—')}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupedDataTable({ rows, client, clientsById, dateField, amountField, columns, emptyText, onRowClick }) {
  const [expanded, setExpanded] = useState(new Set());

  if (!rows || rows.length === 0) {
    return <div style={{ color: '#9ca3af', padding: 24, textAlign: 'center' }}>{emptyText}</div>;
  }

  const groups = {};
  for (const row of rows) {
    // In Group mode `rows` mixes rows from every client in the group, and
    // each row must be filed under ITS OWN client's fiscal year — never the
    // viewed client's — since group members can have different fiscal year
    // ends (Spec: same bug class byYear() already guards against for
    // write-offs). Only fall back to the page's `client` when the row itself
    // carries no client id at all; if the row DOES name a client but that
    // client isn't in clientsById (e.g. its own fetch failed), don't guess —
    // file it under an explicit "Unknown" bucket instead.
    const ownerId = row.client ?? client?.id;
    const owner = clientsById ? clientsById[ownerId] : undefined;

    let key;
    let yearEnd = null;
    let unknownClient = false;
    if (owner) {
      // The year STORED on the row wins over the one its date falls in.
      // Summary by Year, immediately above this table, buckets on that
      // stored field (utils/rollup.js byYear() reads t.year_end /
      // inv.year_end), and Year End is deliberately editable in the
      // time-entry grid -- "a default, not a lock", per WeekView.onChange.
      // Re-deriving it here put a hand-corrected row in one year up the page
      // and a different year down it.
      //
      // The date is still the fallback, because year_end is nullable and
      // blanks are real (64 of the 277 imported Lee Humble invoices have
      // none). byYear() drops those rows from the summary entirely; this
      // table lists them under their date's year instead, since a list that
      // silently omits rows is worse than one that files a blank by date.
      const stored = Number(row.year_end);
      const year = (row.year_end && !Number.isNaN(stored))
        ? stored
        : yearEndForDate(row[dateField], owner.is_corporate, owner.fiscal_month_end);
      yearEnd = yearEndDate(year, owner.is_corporate, owner.fiscal_month_end);
      key = yearEndKey(yearEnd);
    } else {
      key = '__unknown_client__';
      unknownClient = true;
    }

    if (!groups[key]) groups[key] = { key, yearEnd, unknownClient, rows: [], total: 0 };
    groups[key].rows.push(row);
    const v = parseFloat(row[amountField] || 0);
    if (!isNaN(v)) groups[key].total += v;
  }
  const sortedGroups = Object.values(groups).sort((a, b) => {
    if (!a.yearEnd) return 1;
    if (!b.yearEnd) return -1;
    return b.yearEnd - a.yearEnd;
  });

  const toggle = (key) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key); else next.add(key);
    setExpanded(next);
  };

  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
      {sortedGroups.map((g) => (
        <div key={g.key}>
          <div
            onClick={() => toggle(g.key)}
            style={{
              padding: '12px 16px',
              background: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              userSelect: 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#6b7280', fontSize: 14, width: 14 }}>
                {expanded.has(g.key) ? '▼' : '▶'}
              </span>
              <span style={{ fontWeight: 600, color: '#111827' }}>
                {g.unknownClient ? 'Unknown client' : fmtYearEnd(g.yearEnd)}
              </span>
              <span style={{ color: '#6b7280', fontSize: 13 }}>· {g.rows.length} {g.rows.length === 1 ? 'item' : 'items'}</span>
            </div>
            <div style={{ fontWeight: 600, color: '#111827' }}>
              {g.total.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' })}
            </div>
          </div>
          {expanded.has(g.key) && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fff', borderBottom: '1px solid #f3f4f6' }}>
                  {columns.map((c, i) => (
                    <th key={i} style={{ ...th, fontSize: 10 }}>{c.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r, i) => (
                  <tr
                    key={r.id ?? i}
                    onClick={onRowClick ? () => onRowClick(r) : undefined}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: onRowClick ? 'pointer' : 'default' }}
                  >
                    {columns.map((c, j) => (
                      <td key={j} style={{ ...td, fontSize: 13 }}>
                        {c.render ? c.render(r) : (r[c.accessor] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

// The year-END DATE this table shows as its group label, for a fiscal year
// that has already been decided — either read off the row or, failing that,
// derived from its date by utils/rollup.js's yearEndForDate (the same helper
// byYear() uses, so this isn't a fourth copy of that rule).
function yearEndDate(year, isCorporate, fiscalMonthEnd) {
  if (year == null) return null;
  if (!isCorporate || !fiscalMonthEnd) {
    return new Date(year, 11, 31);
  }
  const m = Number(fiscalMonthEnd);
  const lastDay = new Date(year, m, 0).getDate();
  return new Date(year, m - 1, lastDay);
}

function yearEndKey(d) {
  return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : 'unknown';
}

function fmtYearEnd(d) {
  if (!d) return 'No date';
  return `FY ${d.getFullYear()} (ends ${d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })})`;
}


const btnStyle = {
  padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6,
  background: 'white', cursor: 'pointer', fontSize: 14,
};
const th = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', letterSpacing: '0.05em' };
const td = { padding: '10px 12px', fontSize: 14, color: '#111827' };
